import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { useSearchHSNQuery } from '../api/hsnsac.api';
import { Autocomplete, CircularProgress, TextField, Button, Box, AutocompleteProps } from '@mui/material';
import AddBoxIcon from "@mui/icons-material/AddBox";
import { CreateHSNModal } from '../../HSNSACCode/components/CreateHSNModal';

const PAGE_SIZE = 25;

type OptionType = { label: string; value: string; disabled?: boolean };

const determineCodeType = (input: string): string => {
  const trimmedInput = input.trim();
  if (trimmedInput.length === 0) return 'HSN';

  if (trimmedInput.match(/^99/)) {
    return 'SAC';
  }

  if (trimmedInput.match(/^[1-8]/)) {
    return 'HSN';
  }
  return 'HSN';
};

export type HsnCodeAutoCompleteProps = {
  value: string | null;
  onChange: (value: OptionType | null) => void;
  error?: boolean;
  helperText?: string;
  type?: 'HSN' | 'SAC';
  disabled?: boolean;
  label?: string;
  onAddNew?: () => void;
  /**
   * Controls the shrink behavior of the label.
   * When true, the label is always at the top.
   * When false, the label acts as a placeholder and floats on focus/value.
   * @default true
   */
  labelShrink?: boolean;
  /**
   * If true, shows required asterisk
   */
  required?: boolean;
  /**
   * Optional MUI Autocomplete paper/listbox customization.
   * Useful for one-off dropdown UI overrides in specific screens.
   */
  componentsProps?: AutocompleteProps<OptionType, false, false, true>['componentsProps'];
};

export default function HsnCodeAutoComplete({
  value,
  onChange,
  error,
  helperText,
  type,
  disabled = false,
  label,
  onAddNew,
  labelShrink = true,
  required = false,
  componentsProps
}: HsnCodeAutoCompleteProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [inputValueForLabel, setInputValueForLabel] = useState<string>('');
  const [focused, setFocused] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));
  const [allData, setAllData] = useState<OptionType[]>([]);

  // Use value for both create and edit scenarios
  const effectiveValue = value === null || value === undefined || value === 'undefined' ? '' : value;
  const shrinkLabel = labelShrink || !!inputValueForLabel.trim() || focused;
  const searchWord = inputValue || effectiveValue;
  const codeType = type ?? determineCodeType(searchWord);

  const {
    data = [],
    isLoading,
    isFetching,
  } = useSearchHSNQuery(
    {
      rowfrom: (currentPage - 1) * PAGE_SIZE,
      word: searchWord.length >= 3 ? searchWord : effectiveValue && effectiveValue.length >= 1 ? effectiveValue : '',
      codetype: codeType,
      limitno: PAGE_SIZE,
    },
    {
      skip: !loadedPages.has(currentPage) || (searchWord.length < 3 && effectiveValue.length >= 1 && allData.length > 0),
    }
  );

  console.log(data, 'data', allData, 'allData');


  useEffect(() => {
    if (data.length > 0) {
      setAllData((prevData) => {
        const newData = [...prevData];
        const startIndex = (currentPage - 1) * PAGE_SIZE;

        data.forEach((item, index) => {
          newData[startIndex + index] = item;
        });
        return newData.filter((item) => item !== undefined);
      });
    }
  }, [data, currentPage]);

  // Add special option for "Add HSN/SAC Code" button at the top
  const ADD_CODE_OPTION: OptionType = { 
    label: type === 'SAC' ? 'Add SAC Code' : 'Add HSN Code', 
    value: 'ADD_HSN_CODE', 
    disabled: false 
  };
  const options = onAddNew ? [ADD_CODE_OPTION, ...allData] : allData;

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const ul = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = ul;

    if (isLoading || isFetching) return;

    if (scrollTop + clientHeight >= scrollHeight - 2) {
      const nextPage = currentPage + 1;
      if (!loadedPages.has(nextPage)) {
        setLoadedPages((prev) => new Set([...prev, nextPage]));
        setCurrentPage(nextPage);
      }
    }

    if (scrollTop <= 2 && currentPage > 1) {
      const prevPage = currentPage - 1;
      if (!loadedPages.has(prevPage)) {
        setLoadedPages((prev) => new Set([...prev, prevPage]));
        setCurrentPage(prevPage);
      }
    }
  };

  const debounced = useMemo(
    () =>
      debounce((searchText: string) => {
        if (searchText.length >= 3) {
          setCurrentPage(1);
          setLoadedPages(new Set([1]));
          setAllData([]);
          setInputValue(searchText.trim());
        } else {
          setInputValue(searchText.trim());
        }
      }, 500),
    []
  );

  const handleAddNewClick = useCallback(() => {
    setIsModalOpen(true);
    // Also call external handler if provided (for backward compatibility)
    if (onAddNew) {
      onAddNew();
    }
  }, [onAddNew]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleHsnCodeSelected = useCallback((newCode: string) => {
    // Update the form field with the new code
    onChange({ label: newCode, value: newCode });
    setIsModalOpen(false);
  }, [onChange]);

  return (
    <>
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      componentsProps={componentsProps}
      open={open}
      openOnFocus
      onOpen={() => {
        if (disabled) return;
        setOpen(true);
        if (effectiveValue && effectiveValue.length >= 1) {
          const isValueInData = allData.find((item) => item.value === effectiveValue);
          if (!isValueInData) {
            setCurrentPage(1);
            setLoadedPages(new Set([1]));
            setInputValue(effectiveValue);
          }
        }
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={options}
      getOptionLabel={(opt) => {
        if (typeof opt === 'string') return opt;
        if (opt.value === 'ADD_HSN_CODE') return '';
        return opt.label || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (option.value === 'ADD_HSN_CODE' || value?.value === 'ADD_HSN_CODE') return false;
        return option.value === value?.value;
      }}
      value={
        effectiveValue && effectiveValue !== 'ADD_HSN_CODE'
          ? options.find((item) => item.value === effectiveValue && !item.disabled && item.value !== 'ADD_HSN_CODE') ||
            // If the selected value isn't in the loaded options yet (e.g., just created),
            // show it anyway so the input displays the selected code immediately.
            ({ label: effectiveValue, value: effectiveValue } as OptionType)
          : null
      }
      onChange={(e, newVal) => {
        if (disabled) return;
        // Handle "Add HSN Code" button click
        if (newVal && typeof newVal === 'object' && newVal.value === 'ADD_HSN_CODE') {
          handleAddNewClick();
          setOpen(false);
          return;
        }
        if (typeof newVal === 'string') {
          onChange({ label: newVal, value: newVal });
        } else if (newVal && typeof newVal === 'object') {
          onChange(newVal);
        } else {
          setInputValueForLabel('');
          onChange(null);
        }
        setOpen(false);
      }}
      filterOptions={(opts) => opts}
      loading={isLoading}
      freeSolo
      onInputChange={(e, newInput, reason) => {
        if (reason === 'input') {
          setInputValueForLabel(newInput ?? '');
          if (newInput !== 'Please enter at least 3 characters to search') {
            debounced(newInput);
          }
        } else if (reason === 'clear') {
          setInputValueForLabel('');
          setInputValue('');
          setCurrentPage(1);
          setLoadedPages(new Set([1]));
          setAllData([]);
        }
      }}
      ListboxProps={{
        onScroll: handleScroll,
        sx: { 
          minHeight: '200px',
          maxHeight: '200px',
          overflow: 'auto',
          paddingTop: 0,
          paddingBottom: 0,
          '& li:nth-of-type(2)': {
            paddingTop: onAddNew ? '14px' : 0,
          },
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#ccc',
            borderRadius: '3px',
            '&:hover': {
              background: '#999',
            },
          },
        },
      }}
      renderOption={(props, option) => {
        if (option.value === 'ADD_HSN_CODE') {
          return (
            <Box
              component="li"
              {...props}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                backgroundColor: '#ffffff',
                width: '100%',
                padding: 0,
                paddingTop:"10px",
                margin: 0,
                marginBottom: '4px',
                overflow: 'hidden',
                borderBottom: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                minHeight: '18px',
                display: 'block',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: '#ffffff',
                  zIndex: -1,
                },
              }}
            >
              <Button
                fullWidth
                className="dropDownBtncolor"
                variant="text"
                sx={{ 
                  whiteSpace: 'nowrap', 
                  m: 0,
                  p: 0,
                  justifyContent: 'flex-start',
                  height: '18px',
                  minHeight: '18px',
                  backgroundColor: '#ffffff !important',
                  textTransform: 'none',
                  position: 'relative',
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5 !important',
                  },
                  '&:focus': {
                    backgroundColor: '#ffffff !important',
                  },
                  '&:active': {
                    backgroundColor: '#eeeeee !important',
                  },
                }}
                startIcon={<AddBoxIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddNewClick();
                  setOpen(false);
                }}
              >
                {type === 'SAC' ? 'Add SAC Code' : 'Add HSN Code'}
              </Button>
            </Box>
          );
        }
        return (
          <li
            {...props}
            key={option.value}
            style={{
              ...props.style,
              opacity: option.disabled ? 0.6 : 1,
              cursor: option.disabled ? 'not-allowed' : 'default',
              fontStyle: option.disabled ? 'italic' : 'normal',
            }}
          >
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || "HSN/SAC Code"}
          placeholder={shrinkLabel ? " " : undefined}
          required={required}
          InputLabelProps={{
            shrink: shrinkLabel,
          }}
          margin="none"
          inputProps={{
            ...params.inputProps,
            onFocus: (e) => {
              setFocused(true);
              params.inputProps?.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
            },
            onBlur: (e) => {
              setFocused(false);
              params.inputProps?.onBlur?.(e as React.FocusEvent<HTMLInputElement>);
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={!!error}
          helperText={helperText}
          sx={{
            '& .MuiInputBase-input::placeholder': {
              color: 'black',
              opacity: 1,
            },
            ...(required && {
              '.MuiFormLabel-asterisk': {
                color: 'danger.main',
              },
            }),
          }}
        />
      )}
    />
    {isModalOpen && (
      <CreateHSNModal
        open={isModalOpen}
        onClose={handleCloseModal}
        codeType={codeType as 'HSN' | 'SAC' | ''}
        onSelect={handleHsnCodeSelected}
      />
    )}
  </>
  );
}
